import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Grid,
  Select,
  Spin,
  Table,
  Tag,
  notification,
} from "antd";
import { EyeOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { apiRequest } from "util/services";
import EmailPreviewPanel from "./EmailPreviewPanel";
import RecipientCombobox from "./RecipientCombobox";
import styles from "./style.module.scss";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const DATE_PRESETS = {
  last7: "last7",
  last30: "last30",
  thisMonth: "thisMonth",
  custom: "custom",
};

const resolvePreviousCalendarWeek = () => {
  let start = dayjs().subtract(7, "day");
  while (start.day() !== 1) {
    start = start.subtract(1, "day");
  }
  return [start, start.add(6, "day")];
};

const resolvePresetRange = (preset) => {
  const today = dayjs().startOf("day");
  if (preset === DATE_PRESETS.last7) {
    return [today.subtract(6, "day"), today];
  }
  if (preset === DATE_PRESETS.last30) {
    return [today.subtract(29, "day"), today];
  }
  if (preset === DATE_PRESETS.thisMonth) {
    return [today.startOf("month"), today];
  }
  return resolvePreviousCalendarWeek();
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm") : "—";
};

const Representation = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [memberCandidates, setMemberCandidates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [datePreset, setDatePreset] = useState(DATE_PRESETS.last7);
  const [dateRange, setDateRange] = useState(() => resolvePresetRange(DATE_PRESETS.last7));
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async (organizationId) => {
    if (!organizationId) return;
    setHistoryLoading(true);
    try {
      const res = await apiRequest(
        `welcome/organization-activity-report/history?limit=20&sync=1&organization_id=${encodeURIComponent(organizationId)}`,
        "GET",
        null,
        true,
      );
      if (res?.error) {
        if (res.status !== 403) {
          notification.error({ message: res.error });
        }
        return;
      }
      setHistory(Array.isArray(res?.success) ? res.success : []);
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async (organizationId, { showPageLoader = false } = {}) => {
    if (showPageLoader) {
      setLoading(true);
    }
    setForbidden(false);
    try {
      const query = organizationId
        ? `?organization_id=${encodeURIComponent(organizationId)}`
        : "";
      const res = await apiRequest(
        `welcome/organization-email-settings${query}`,
        "GET",
        null,
        true,
      );
      if (res?.error) {
        if (res.status === 403 || res.error === "FORBIDDEN") {
          setForbidden(true);
          return;
        }
        notification.error({ message: res.error });
        return;
      }
      const payload = res?.success || {};
      const nextRecipients = Array.isArray(payload.activity_report_recipient_emails)
        ? payload.activity_report_recipient_emails
        : [];
      const nextOrganizations = Array.isArray(res?.organizations) ? res.organizations : [];
      let nextOrganizationId =
        res?.selected_organization_id ||
        payload.organization_id ||
        nextOrganizations[0]?.organization_id ||
        null;
      if (
        nextOrganizationId &&
        nextOrganizations.length > 0 &&
        !nextOrganizations.some((org) => org.organization_id === nextOrganizationId)
      ) {
        nextOrganizationId =
          nextOrganizations.find(
            (org) =>
              String(org.organization_name || "").trim().toLowerCase() ===
              "guest experience",
          )?.organization_id || nextOrganizations[0]?.organization_id || null;
      }

      setOrganizations(nextOrganizations);
      setSelectedOrganizationId(nextOrganizationId);
      setMemberCandidates(Array.isArray(res?.member_candidates) ? res.member_candidates : []);
      setRecipients(nextRecipients);
      setCanEdit(Boolean(res?.can_edit_organization_email_settings));
      if (res?.can_edit_organization_email_settings && nextOrganizationId) {
        await loadHistory(nextOrganizationId);
      } else {
        setHistory([]);
      }
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  }, [loadHistory]);

  useEffect(() => {
    loadSettings(undefined, { showPageLoader: true });
  }, [loadSettings]);

  const handleOrganizationChange = async (organizationId) => {
    if (!organizationId || organizationId === selectedOrganizationId) return;
    setSelectedOrganizationId(organizationId);
    setPreviewOpen(false);
    setPreviewHtml(null);
    setPreviewSubject(null);
    await loadSettings(organizationId);
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== DATE_PRESETS.custom) {
      setDateRange(resolvePresetRange(preset));
    }
  };

  const handleDateRangeChange = (values) => {
    if (values?.[0] && values?.[1]) {
      setDatePreset(DATE_PRESETS.custom);
      setDateRange(values);
    }
  };

  const statusLabel = useCallback(
    (status) => t(`email_reports_page.status_${status}`, status),
    [t],
  );

  const recipientSuggestions = useMemo(() => {
    const fromMembers = (memberCandidates || [])
      .map((member) => String(member?.email || "").trim().toLowerCase())
      .filter(Boolean);
    const fromHistory = (history || []).flatMap((send) =>
      (send.recipients || []).map((recipient) => recipient.recipient_email),
    );
    return [...new Set([...fromMembers, ...recipients, ...fromHistory].filter(Boolean))];
  }, [memberCandidates, history, recipients]);

  const historyRows = useMemo(
    () =>
      (history || []).flatMap((send) =>
        (send.recipients || []).map((recipient) => ({
          key: `${send.id}-${recipient.id}`,
          sendId: send.id,
          period: `${send.period_start} – ${send.period_end}`,
          sentAt: send.sent_at,
          recipient: recipient.recipient_email,
          status: recipient.delivery_status,
          deliveredAt: recipient.delivered_at,
          openedAt: recipient.opened_at,
          openCount: recipient.open_count,
          clickCount: recipient.click_count,
          clickEvents: recipient.click_events || [],
          bounceReason: recipient.bounce_reason,
        })),
      ),
    [history],
  );

  const buildReportPayload = () => ({
    organization_id: selectedOrganizationId,
    start_date: dateRange[0].format("YYYY-MM-DD"),
    end_date: dateRange[1].format("YYYY-MM-DD"),
    recipient_emails: recipients,
    force_send: true,
  });

  const handlePreview = async () => {
    if (!canEdit || !dateRange?.[0] || !dateRange?.[1] || !selectedOrganizationId) return;
    if (recipients.length === 0) {
      notification.warning({ message: t("email_reports_page.sendNoRecipients") });
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml(null);
    setPreviewSubject(null);

    try {
      const res = await apiRequest(
        "welcome/organization-activity-report/send",
        "POST",
        {
          ...buildReportPayload(),
          dry_run: true,
        },
        true,
      );
      if (res?.error) {
        const msg =
          res.error === "no_recipients"
            ? t("email_reports_page.sendNoRecipients")
            : res.error === "no_activity"
              ? t("email_reports_page.sendNoActivity")
              : res.message || res.error;
        notification.warning({ message: msg });
        setPreviewOpen(false);
        return;
      }
      const row = res?.result || (Array.isArray(res?.organizations) ? res.organizations[0] : null);
      const htmlBody = row?.html_body || res?.html_body;
      if (!htmlBody) {
        notification.error({ message: t("email_reports_page.previewFailed") });
        setPreviewOpen(false);
        return;
      }
      setPreviewHtml(htmlBody);
      setPreviewSubject(row?.subject || res?.subject || "");
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendNow = async () => {
    if (!canEdit || !dateRange?.[0] || !dateRange?.[1] || !selectedOrganizationId) return;
    if (recipients.length === 0) {
      notification.warning({ message: t("email_reports_page.sendNoRecipients") });
      return;
    }
    setSending(true);
    try {
      const res = await apiRequest(
        "welcome/organization-activity-report/send",
        "POST",
        buildReportPayload(),
        true,
      );
      if (res?.error) {
        if (res.error === "quota_exceeded") {
          notification.error({
            message: t("email_reports_page.sendQuotaExceeded"),
          });
          return;
        }
        const msg =
          res.error === "no_recipients"
            ? t("email_reports_page.sendNoRecipients")
            : res.error === "no_activity"
              ? t("email_reports_page.sendNoActivity")
              : res.message || res.error;
        notification.warning({ message: msg });
        return;
      }
      if (res?.ok === false) {
        notification.error({ message: t("email_reports_page.sendFailed") });
        return;
      }
      notification.success({ message: t("email_reports_page.sendSuccess") });
      await loadHistory(selectedOrganizationId);
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      title: t("email_reports_page.colPeriod"),
      dataIndex: "period",
      key: "period",
      width: 190,
    },
    {
      title: t("email_reports_page.colRecipient"),
      dataIndex: "recipient",
      key: "recipient",
    },
    {
      title: t("email_reports_page.colStatus"),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "opened" || status === "clicked" ? "green" : status === "bounced" || status === "failed" ? "red" : "blue"}>
          {statusLabel(status)}
        </Tag>
      ),
    },
    {
      title: t("email_reports_page.colSentAt"),
      dataIndex: "sentAt",
      key: "sentAt",
      render: formatDateTime,
    },
    {
      title: t("email_reports_page.colOpenedAt"),
      dataIndex: "openedAt",
      key: "openedAt",
      render: (value, row) =>
        value
          ? `${formatDateTime(value)}${row.openCount > 1 ? ` (${row.openCount}×)` : ""}`
          : "—",
    },
    {
      title: t("email_reports_page.colClicks"),
      dataIndex: "clickCount",
      key: "clickCount",
      render: (count, row) =>
        count > 0
          ? row.clickEvents
              .map((event) => event.url || t("email_reports_page.link"))
              .join(", ")
          : "—",
    },
  ];

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <Spin />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.forbidden}>{t("email_reports_page.forbidden")}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.pageLayout} ${previewOpen && !isMobile ? styles.pageLayoutWithPreview : ""}`}>
      <div className={styles.mainColumn}>
        <p className={styles.breadcrumb}>{t("email_reports_page.breadcrumb")}</p>
        <h1 className={styles.title}>{t("email_reports_page.title")}</h1>
        <p className={styles.subtitle}>{t("email_reports_page.subtitle")}</p>

        {!canEdit ? (
          <Alert
            type="info"
            showIcon
            message={t("email_reports_page.readOnlyTitle")}
            description={t("email_reports_page.readOnlyHint")}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <div className={styles.formCard}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t("email_reports_page.organizationTitle")}</label>
            <p className={styles.fieldHint}>{t("email_reports_page.organizationHint")}</p>
            <Select
              className={styles.orgSelect}
              value={selectedOrganizationId || undefined}
              placeholder={t("email_reports_page.organizationPlaceholder")}
              onChange={handleOrganizationChange}
              disabled={organizations.length <= 1 || !canEdit}
              showSearch
              optionFilterProp="label"
              options={organizations.map((org) => ({
                value: org.organization_id,
                label: org.organization_name || org.organization_id,
              }))}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t("email_reports_page.recipientsTitle")}</label>
            <p className={styles.fieldHint}>{t("email_reports_page.recipientsHint")}</p>
            <RecipientCombobox
              value={recipients}
              onChange={setRecipients}
              suggestions={recipientSuggestions}
              memberCandidates={memberCandidates}
              disabled={!canEdit}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t("email_reports_page.dateRangeTitle")}</label>
            <p className={styles.fieldHint}>{t("email_reports_page.dateRangeHint")}</p>
            <div className={styles.presetRow}>
              {[
                { key: DATE_PRESETS.last7, label: t("email_reports_page.presetLast7") },
                { key: DATE_PRESETS.last30, label: t("email_reports_page.presetLast30") },
                { key: DATE_PRESETS.thisMonth, label: t("email_reports_page.presetThisMonth") },
                { key: DATE_PRESETS.custom, label: t("email_reports_page.presetCustom") },
              ].map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`${styles.presetButton} ${datePreset === preset.key ? styles.presetButtonActive : ""}`}
                  disabled={!canEdit}
                  onClick={() => handlePresetChange(preset.key)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={styles.dateRow}>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                allowClear={false}
                format="DD-MM-YYYY"
                disabled={!canEdit || datePreset !== DATE_PRESETS.custom}
              />
            </div>
          </div>

          {canEdit ? (
            <div className={styles.formFooter}>
              <Button
                icon={<EyeOutlined />}
                loading={previewLoading}
                onClick={handlePreview}
                disabled={sending}
              >
                {t("email_reports_page.preview")}
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                onClick={handleSendNow}
                disabled={previewLoading}
              >
                {t("email_reports_page.sendNow")}
              </Button>
            </div>
          ) : null}
        </div>

        <section className={styles.historySection}>
          <div className={styles.historyHeader}>
            <div>
              <h2 className={styles.sectionTitle}>{t("email_reports_page.historyTitle")}</h2>
              <p className={styles.fieldHint}>{t("email_reports_page.historyHint")}</p>
            </div>
            <Button
              icon={<ReloadOutlined />}
              loading={historyLoading}
              onClick={() => loadHistory(selectedOrganizationId)}
              disabled={!selectedOrganizationId}
            >
              {t("email_reports_page.refreshHistory")}
            </Button>
          </div>
          <Alert
            type="info"
            showIcon
            message={t("email_reports_page.analyticsNoteTitle")}
            description={t("email_reports_page.analyticsNote")}
            style={{ marginBottom: 16 }}
          />
          <Table
            size="small"
            loading={historyLoading}
            columns={columns}
            dataSource={historyRows}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: t("email_reports_page.historyEmpty") }}
            scroll={{ x: 900 }}
          />
        </section>
      </div>

      <EmailPreviewPanel
        open={previewOpen}
        loading={previewLoading}
        html={previewHtml}
        subject={previewSubject}
        onClose={() => setPreviewOpen(false)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default Representation;
